import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { PenTool, ExternalLink } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { buildSignatureOrderPath, getSignatureFeatures } from '@/helpers/signatureOrderHelpers';

/** Assinatura RV is prioritized as the first option followed by DocuSign */
const SIGNATURE_OPTIONS = [
  { type: 'marketplace_bry', feature: 'hasBry', titleId: 'sendToBry' },
  { type: 'marketplace_docusign', feature: 'hasDocuSign', titleId: 'sendToDocuSign' },
];

const ReportSignatureAction = ({ orderId, reportHash }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const user = useUser();
  const features = useMemo(() => getSignatureFeatures(user), [user]);
  const options = useMemo(() => SIGNATURE_OPTIONS.filter(option => features[option.feature]), [features]);
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setCoords(null);
      return undefined;
    }

    const updateCoords = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 110;
      setCoords({
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };

    updateCoords();

    const handleClickOutside = event => {
      if (
        containerRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleScrollOrResize = () => setIsOpen(false);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  if (options.length === 0) return null;

  const openForm = orderType => {
    setIsOpen(false);
    navigate(buildSignatureOrderPath(orderType, orderId, reportHash));
  };

  // A single available provider navigates directly; two providers open a menu
  const single = options.length === 1 ? options[0] : null;
  const handleTrigger = () => (single ? openForm(single.type) : setIsOpen(prev => !prev));

  return (
    <div className="relative inline-flex items-center" ref={containerRef}>
      <span
        role="button"
        tabIndex={0}
        title={intl.formatMessage({ id: single ? single.titleId : 'sendToSignature' })}
        onClick={handleTrigger}
        onKeyDown={e => e.key === 'Enter' && handleTrigger()}
        className="cursor-pointer flex items-center hover:text-[#3a79bf] transition-colors"
      >
        <PenTool size={16} />
      </span>

      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: coords.top,
            bottom: coords.bottom,
            right: coords.right,
            zIndex: 9999,
          }}
          className="min-w-[160px] rounded-md border border-gray-200 bg-white py-1 shadow-lg font-['Open_Sans']"
        >
          <div className="flex flex-col">
            {options.map(option => (
              <button
                type="button"
                key={option.type}
                className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 hover:text-[#0485BE] transition-colors text-left"
                onClick={() => openForm(option.type)}
              >
                <span className="whitespace-nowrap">{intl.formatMessage({ id: option.type })}</span>
                <ExternalLink size={12} className="ml-3 shrink-0 text-gray-400" />
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

ReportSignatureAction.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  reportHash: PropTypes.string.isRequired,
};

export default ReportSignatureAction;
